/**
 * Auto-recover tabs that straddle a deploy (2026-07-21).
 *
 * Deploys publish a new build whose lazy chunks have new hashed names.
 * A tab opened before the deploy asks for the OLD chunk names on its
 * next navigation; if those files are gone (or the tab is very old),
 * the dynamic import fails and the user sees a broken page with a
 * generic error ("Failed to initialize data" — Matthew's blank Home).
 *
 * The deploy side now keeps prior builds' hashed assets around for 14
 * days, so this reload is the second line of defense: if a chunk load
 * still fails, reload once to pick up the fresh index.html + bundle.
 * A sessionStorage timestamp guards against reload loops (a genuinely
 * broken build must not strobe the browser).
 */

const RELOAD_GUARD_KEY = "dms_chunk_reload_at";
const RELOAD_GUARD_WINDOW_MS = 60_000;

const CHUNK_ERROR_PATTERNS = [
  /Loading chunk [\w-]+ failed/i,
  /Loading CSS chunk [\w-]+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
];

export const isChunkLoadError = (reasonOrEvent) => {
  const candidate =
    reasonOrEvent?.reason ?? reasonOrEvent?.error ?? reasonOrEvent;
  if (!candidate) return false;
  if (candidate.name === "ChunkLoadError") return true;
  const message = String(candidate.message ?? candidate);
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
};

const reloadOnce = () => {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
    if (Date.now() - last < RELOAD_GUARD_WINDOW_MS) {
      console.error(
        "[stale-chunk] Chunk load failed again within the guard window; not reloading."
      );
      return;
    }
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable — still better to reload than strand.
  }
  console.warn("[stale-chunk] Stale build detected; reloading for the new one.");
  globalThis.location.reload();
};

export const installStaleChunkReload = () => {
  globalThis.addEventListener("unhandledrejection", (event) => {
    if (isChunkLoadError(event)) {
      event.preventDefault();
      reloadOnce();
    }
  });
  globalThis.addEventListener("error", (event) => {
    if (isChunkLoadError(event)) {
      reloadOnce();
    }
  });
};
